import { put, call, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { setUser } from '../slices/auth';
import { isStaff } from '../../lib/roles';
import { errMessage } from '../../lib/apiError';
import { selectMyProfile } from '../selectors/teamProfile';
import { profileFieldErrors, isProfileForbidden } from '../../lib/teamProfile';
import { walkthroughFailed, walkthroughCompleted, completeWalkthroughAction } from '../slices/tour';
import {
  saveMyProfile,
  fetchMyProfile,
  completeWalkthrough,
  uploadProfilePicture,
} from '../apis/teamProfile';
import {
  myProfileSaved,
  myProfileLoaded,
  pictureUploaded,
  myProfileFailure,
  getMyProfileAction,
  saveMyProfileAction,
  uploadProfilePictureAction,
} from '../slices/teamProfile';

function* reportFailure(error, fallback) {
  const forbidden = isProfileForbidden(error);
  yield put(
    myProfileFailure({
      forbidden,
      fieldErrors: profileFieldErrors(error),
      // 403 has no machine-readable code here, so the copy is ours rather than DRF's.
      message: forbidden
        ? 'Your account has no active staff profile, so there is no card to manage.'
        : errMessage(error, fallback),
    })
  );
}

function* handleGet() {
  try {
    const profile = yield call(fetchMyProfile);
    yield put(myProfileLoaded(profile));
  } catch (error) {
    yield* reportFailure(error, 'Failed to load your profile');
  }
}

function* handleSave(action) {
  try {
    const profile = yield call(saveMyProfile, action.payload);
    yield put(myProfileSaved({ profile, savedAt: new Date().toISOString() }));
  } catch (error) {
    yield* reportFailure(error, 'Failed to save your profile');
  }
}

function* handleUpload(action) {
  try {
    yield call(uploadProfilePicture, action.payload);
    yield put(pictureUploaded());
    // profile_image_url is a short-lived signed URL minted per request, so the card is
    // re-read rather than patched with anything the upload returned.
    yield put(getMyProfileAction());
  } catch (error) {
    yield* reportFailure(error, 'Failed to upload the picture');
  }
}

// The tour's start condition needs the card at sign-in; MyProfileView reads it only on its page.
function* handleSignedIn(action) {
  if (!isStaff(action.payload)) return;
  // Logout resets this slice, so loaded or loading here means this sign-in already read it.
  const { loaded, loading } = yield select(selectMyProfile);
  if (!loaded && !loading) yield put(getMyProfileAction());
}

function* handleWalkthrough() {
  try {
    const profile = yield call(completeWalkthrough);
    yield put(walkthroughCompleted(profile));
  } catch (error) {
    yield put(walkthroughFailed(errMessage(error, 'Could not record that you have seen the tour')));
  }
}

export default function* teamProfileSaga() {
  yield takeLatest(setUser.type, handleSignedIn);
  yield takeLeading(completeWalkthroughAction.type, handleWalkthrough);
  yield takeLatest(getMyProfileAction.type, handleGet);
  yield takeLatest(saveMyProfileAction.type, handleSave);
  yield takeLatest(uploadProfilePictureAction.type, handleUpload);
}
